import { useState } from 'react'

import { needsUpgrade } from '@/shared/api'
import { useRestartSurvey, useUploadDocument } from '@/shared/api/queries'
import { joinSides } from '@/shared/image'
import './sides.css'
import './wizard.css'
import type { Analysis } from '@/shared/api/queries'
import { formatFullDate } from '@/shared/format'
import { useLocalization } from '@/shared/i18n'
import type { TranslationKey } from '@/shared/i18n'
import { DOCUMENT_TYPES, documentIcon, documentSurvey, documentTypeLabel } from '@/shared/model'
import { Alert, Button, Card, ErrorDetails, Icon, Stack, Text } from '@/shared/ui'
import { useKeepLocally } from '@/shared/vault'

const ACCEPT = 'application/pdf,image/jpeg,image/png'
const STEPS = 3

export function UploadForm({
  onUploaded,
  onQuotaExceeded,
  onSurvey,
  onClose,
}: {
  onUploaded: (analysis: Analysis) => void
  onQuotaExceeded: () => void
  onSurvey: (tag: string) => void
  onClose: () => void
}) {
  const { t, language } = useLocalization()

  const [type, setType] = useState<string>()
  const [done, setDone] = useState<Analysis>()
  const [sides, setSides] = useState<File[]>([])

  const upload = useUploadDocument()
  const restart = useRestartSurvey()

  function choose(kind: string) {
    const survey = documentSurvey(kind)

    if (!survey) {
      setType(kind)

      return
    }

    restart.mutate(survey, { onSuccess: () => onSurvey(survey) })
  }
  const { keep } = useKeepLocally()

  // Both sides are joined into one picture before upload: the back carries the
  // machine readable zone and, on a permit, the ARC and the date of issue; the
  // front carries the number and the expiry. One reading of one document beats
  // two halves nobody can merge afterwards.
  function submit() {
    if (sides.length === 0 || !type) {
      return
    }

    void joinSides(sides).then(send)
  }

  function send(file: File | undefined) {
    if (!file || !type) {
      return
    }

    upload.mutate(
      { type, file },
      {
        onSuccess: (analysis) => {
          // The file and the number in full stay on this device: the answer
          // carries the number once and the server keeps only its mask.
          keep({
            documentId: analysis.document.id,
            file,
            fileName: file.name,
            ...(analysis.revealed?.document_number
              ? { number: analysis.revealed.document_number }
              : {}),
          })

          setDone(analysis)
          setSides([])
        },
        onError: (error) => {
          if (needsUpgrade(error)) {
            onQuotaExceeded()
          }
        },
      },
    )
  }

  const taken = sides.filter(Boolean).length
  const step = done ? STEPS : type ? 2 : 1

  function back() {
    if (type && !done) {
      setType(undefined)
      setSides([])

      return
    }

    onClose()
  }

  return (
    <div className="wizard" role="dialog" aria-modal="true" aria-label={t('documents.add')}>
      <div className="wizard__bar">
        <button className="wizard__back" type="button" onClick={back} aria-label={t('app.back')}>
          ←
        </button>
        <div className="wizard__steps">
          {[1, 2, 3].map((one) => (
            <span
              key={one}
              className={
                one === step
                  ? 'wizard__step wizard__step--current'
                  : one < step
                    ? 'wizard__step wizard__step--done'
                    : 'wizard__step'
              }
            />
          ))}
        </div>
        <span className="wizard__back" aria-hidden="true" />
      </div>

      <div className="wizard__body">
        <Stack gap="md">
          <Stack gap="xs">
            <Text role="eyebrow" tone="muted">
              {t('onboarding.step', { step, total: STEPS })}
            </Text>
            <Text as="h2" role="heading-2">
              {done ? (
                t('documents.upload.added')
              ) : type ? (
                <>
                  <Icon size="lg">{documentIcon(type)}</Icon> {t(documentTypeLabel(type))}
                </>
              ) : (
                t('documents.upload.step.type')
              )}
            </Text>
          </Stack>

          {done && done.missing && done.missing.length > 0 ? (
            <Card>
              <Stack direction="row" gap="sm">
                <Icon size="lg">🔄</Icon>
                <Stack gap="xs">
                  <Text role="body-strong">{t('documents.upload.other_side')}</Text>
                  <Text role="caption" tone="muted">
                    {t('documents.upload.other_side.body', {
                      fields: done.missing.map((key) => t(fieldLabel(key))).join(', '),
                    })}
                  </Text>
                </Stack>
              </Stack>
              <label className="upload">
                <span className="upload__label">{t('documents.upload.other_side.action')}</span>
                <input
                  type="file"
                  accept={ACCEPT}
                  capture="environment"
                  disabled={upload.isPending}
                  onChange={(event) => send(event.target.files?.[0])}
                />
              </label>
              <Button variant="quiet" full onClick={() => onUploaded(done)}>
                {t('documents.upload.open')}
              </Button>
            </Card>
          ) : done ? (
            <Card>
              <Stack direction="row" gap="sm">
                <Icon size="lg">✅</Icon>
                <Stack gap="xs">
                  <Text role="body-strong">{t(documentTypeLabel(done.document.type))}</Text>
                  <Text role="caption" tone="muted">
                    {done.document.expires_on
                      ? t('documents.expires', {
                          date: formatFullDate(done.document.expires_on, language),
                        })
                      : t('documents.no_dates')}
                  </Text>
                </Stack>
              </Stack>
              <Button full onClick={() => onUploaded(done)}>
                {t('documents.upload.open')}
              </Button>
            </Card>
          ) : null}

          {!done && !type ? (
            <ul className="grid-2">
              {DOCUMENT_TYPES.map((kind) => (
                <li key={kind}>
                  <button
                    className="tile"
                    type="button"
                    disabled={restart.isPending}
                    onClick={() => choose(kind)}
                  >
                    <Icon size="lg">{documentIcon(kind)}</Icon>
                    <Text as="span" role="label">
                      {t(documentTypeLabel(kind))}
                    </Text>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {!done && type ? (
            <Stack gap="md">
              <div className="grid-2">
                {[0, 1].map((slot) => {
                  const chosen = sides[slot]

                  return (
                    <label key={slot} className={chosen ? 'side side--filled' : 'side'}>
                      <Icon size="lg">{chosen ? '✅' : slot === 0 ? '🪪' : '🔢'}</Icon>
                      <Text role="label">
                        {slot === 0 ? t('documents.side.front') : t('documents.side.back')}
                      </Text>
                      <Text role="caption" tone="muted">
                        {chosen
                          ? chosen.name
                          : slot === 0
                            ? t('documents.side.front.holds')
                            : t('documents.side.back.holds')}
                      </Text>
                      <input
                        className="visually-hidden"
                        type="file"
                        accept={ACCEPT}
                        {...(slot === 0 ? { capture: 'environment' as const } : {})}
                        disabled={upload.isPending}
                        onChange={(event) => {
                          const picked = event.target.files?.[0]

                          if (picked) {
                            setSides((kept) => {
                              const next = [...kept]
                              next[slot] = picked

                              return next
                            })
                          }
                        }}
                      />
                    </label>
                  )
                })}
              </div>
            </Stack>
          ) : null}

          {restart.error ? (
            <Alert status="danger" title={restart.error.message}>
              <ErrorDetails error={restart.error} />
            </Alert>
          ) : null}

          {upload.error && !needsUpgrade(upload.error) ? (
            <Alert status="danger" title={upload.error.message}>
              <ErrorDetails error={upload.error} />
            </Alert>
          ) : null}
        </Stack>
      </div>

      {type && !done ? (
        <div className="wizard__footer">
          <Button full disabled={taken === 0 || upload.isPending} onClick={submit}>
            {upload.isPending
              ? t('documents.upload.reading')
              : t('documents.upload.send', { count: taken })}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

// fieldLabel names a field the way the upload screen has to say it out loud —
// «this side does not carry the ARC and the date of issue» — rather than by the
// key the API uses.
function fieldLabel(key: string): TranslationKey {
  return `documents.field.${key}` as TranslationKey
}
